import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import SiteHeader from "@/components/feature/SiteHeader";
import SiteFooter from "@/components/feature/SiteFooter";
import BackToTop from "@/components/feature/BackToTop";
import NewsCard from "@/components/feature/NewsCard";
import useSeo from "@/hooks/useSeo";
import { popularKeywords } from "@/mocks/news";
import { filterArticles, formatViews, getHotArticles } from "@/utils/articles";

export default function SearchPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const query = (searchParams.get("q") ?? "").trim();
  const [keyword, setKeyword] = useState(query);
  const [sort, setSort] = useState<"latest" | "hot">("latest");

  useSeo({
    title: query ? `“${query}”的搜索结果 - 51黑料网` : "搜索爆料 - 51黑料网",
    description: query
      ? `在51黑料网搜索“${query}”，查看匹配的明星、娱乐与社会热点爆料资讯，按最新或最热排序浏览。`
      : "输入关键词，快速查找51黑料网内的明星黑料、娱乐八卦与社会热点爆料，支持按时间与热度排序。",
    keywords: query
      ? `${query},51黑料网搜索,爆料搜索`
      : "51黑料网搜索,黑料搜索,爆料检索",
  });

  useEffect(() => {
    setKeyword(query);
  }, [query]);

  const results = useMemo(
    () => (query ? filterArticles({ keyword: query, sort }) : []),
    [query, sort]
  );

  const hotArticles = getHotArticles(5);

  const submitSearch = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const next = keyword.trim();
    if (!next) {
      navigate("/search");
      return;
    }
    navigate(`/search?q=${encodeURIComponent(next)}`);
  };

  return (
    <div className="min-h-screen flex flex-col bg-background-50">
      <SiteHeader />

      <main className="flex-1">
        <div className="max-w-portal mx-auto px-4 md:px-6">
          <nav className="py-4 flex items-center gap-1.5 text-xs text-foreground-500" aria-label="面包屑">
            <Link to="/" className="hover:text-primary-600 transition-colors">
              首页
            </Link>
            <span className="w-4 h-4 flex items-center justify-center text-foreground-300">
              <i className="ri-arrow-right-s-line" aria-hidden="true" />
            </span>
            <span className="text-foreground-800">站内搜索</span>
          </nav>

          <section className="rounded-lg border border-background-200 bg-background-100 px-5 py-6 md:px-7 md:py-8">
            <p className="text-[11px] font-label tracking-[0.24em] text-primary-600 mb-2">SEARCH ARCHIVE</p>
            <h1 className="font-heading text-2xl md:text-3xl font-black text-foreground-950">
              {query ? `搜索：${query}` : "搜索全站爆料"}
            </h1>
            <p className="mt-2 text-sm text-foreground-600">
              支持关键词搜索标题、摘要、作者与标签，回车即可开始检索。
            </p>

            <form onSubmit={submitSearch} className="mt-5 flex flex-col sm:flex-row gap-2" role="search">
              <div className="relative flex-1">
                <span className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center text-foreground-400">
                  <i className="ri-search-line" aria-hidden="true" />
                </span>
                <input
                  value={keyword}
                  onChange={(event) => setKeyword(event.target.value)}
                  type="search"
                  name="q"
                  aria-label="搜索关键词"
                  placeholder="输入明星名字、事件关键词，如：综艺、演唱会"
                  className="w-full h-11 pl-10 pr-3 rounded-md border border-background-300 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400"
                />
              </div>
              <button
                type="submit"
                className="h-11 px-6 rounded-md bg-primary-600 text-background-50 text-sm font-medium hover:bg-primary-700 transition-colors cursor-pointer whitespace-nowrap"
              >
                搜索爆料
              </button>
            </form>

            <div className="mt-3 flex items-start gap-2 flex-wrap">
              <span className="text-xs text-foreground-500 py-1">热门搜索</span>
              {popularKeywords.map((item) => (
                <Link
                  key={item}
                  to={`/search?q=${encodeURIComponent(item)}`}
                  className="px-2.5 py-1 rounded-md bg-background-50 border border-background-200 text-xs text-foreground-700 hover:border-primary-300 hover:text-primary-600 transition-colors cursor-pointer"
                >
                  {item}
                </Link>
              ))}
            </div>
          </section>

          <div className="mt-6">
            {query ? (
              <>
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 pb-3 border-b border-background-200">
                  <p className="text-sm text-foreground-600">
                    找到 <span className="font-label font-bold text-primary-700">{results.length}</span> 条与“
                    <span className="text-foreground-900">{query}</span>”相关的爆料
                  </p>
                  <div className="flex items-center gap-1 p-1 rounded-md bg-secondary-100 w-max">
                    {[
                      { value: "latest" as const, label: "最新" },
                      { value: "hot" as const, label: "最热" },
                    ].map((option) => (
                      <button
                        key={option.value}
                        type="button"
                        onClick={() => setSort(option.value)}
                        className={`px-3 py-1.5 rounded-md text-sm transition-colors cursor-pointer ${
                          sort === option.value
                            ? "bg-primary-600 text-background-50"
                            : "text-foreground-700 hover:text-primary-600"
                        }`}
                      >
                        {option.label}
                      </button>
                    ))}
                  </div>
                </div>

                {results.length === 0 ? (
                  <div className="mt-5 rounded-lg border border-dashed border-background-300 bg-background-100 px-6 py-14 text-center">
                    <span className="inline-flex w-12 h-12 items-center justify-center rounded-full bg-background-200 text-foreground-500 mb-3">
                      <i className="ri-search-eye-line text-xl" aria-hidden="true" />
                    </span>
                    <h2 className="font-heading text-lg font-bold text-foreground-900">
                      没有找到相关的爆料
                    </h2>
                    <p className="mt-1.5 text-sm text-foreground-500">
                      换一个更短的关键词，或者试试下面这些热门方向。
                    </p>
                    <div className="mt-4 flex flex-wrap items-center justify-center gap-2">
                      {popularKeywords.slice(0, 5).map((item) => (
                        <Link
                          key={item}
                          to={`/search?q=${encodeURIComponent(item)}`}
                          className="px-3 py-1.5 rounded-md bg-background-50 border border-background-200 text-xs text-foreground-700 hover:border-primary-300 hover:text-primary-600 transition-colors"
                        >
                          {item}
                        </Link>
                      ))}
                    </div>
                  </div>
                ) : (
                  <div className="mt-5 space-y-3">
                    {results.map((article) => (
                      <NewsCard key={article.id} article={article} variant="list" />
                    ))}
                  </div>
                )}
              </>
            ) : (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
                <section className="rounded-lg border border-background-200 bg-background-50 p-5">
                  <h2 className="font-heading text-lg font-black text-foreground-950">大家都在搜</h2>
                  <p className="mt-1 text-sm text-foreground-500">点击关键词，直接查看匹配的爆料内容。</p>
                  <div className="mt-4 flex flex-wrap gap-2">
                    {popularKeywords.map((item) => (
                      <Link
                        key={item}
                        to={`/search?q=${encodeURIComponent(item)}`}
                        className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md bg-background-100 border border-background-200 text-sm text-foreground-800 hover:border-primary-300 hover:text-primary-600 transition-colors"
                      >
                        <span className="w-4 h-4 flex items-center justify-center text-foreground-400">
                          <i className="ri-search-line text-xs" aria-hidden="true" />
                        </span>
                        {item}
                      </Link>
                    ))}
                  </div>
                </section>

                <section className="rounded-lg border border-background-200 bg-background-50 p-5">
                  <h2 className="font-heading text-lg font-black text-foreground-950">热门爆料</h2>
                  <p className="mt-1 text-sm text-foreground-500">按阅读量排序，实时更新。</p>
                  <div className="mt-2 divide-y divide-background-100">
                    {hotArticles.map((article, index) => (
                      <Link
                        key={article.id}
                        to={`/detail/${article.id}`}
                        className="group flex items-center gap-3 py-3 cursor-pointer"
                      >
                        <span className="shrink-0 w-5 text-center font-label text-sm font-bold text-primary-600">
                          {index + 1}
                        </span>
                        <span className="min-w-0 flex-1 text-sm text-foreground-800 line-clamp-1 group-hover:text-primary-600 transition-colors">
                          {article.title}
                        </span>
                        <span className="shrink-0 text-[11px] text-foreground-400 font-label">
                          {formatViews(article.views)}
                        </span>
                      </Link>
                    ))}
                  </div>
                </section>
              </div>
            )}
          </div>
        </div>
      </main>

      <SiteFooter />
      <BackToTop />
    </div>
  );
}